import { useMemo, useState, useEffect } from "react";
import { ChevronDown, Circle, CheckCircle, MinusCircle } from "lucide-react";
import { BaseChatDtoCourse } from "@packages/api/madlenAPI.schemas";
import { useGetTopics } from "@packages/api/madlenAPI";
import { Button, Text } from "@core/index";
import { useTranslation } from "react-i18next";

interface UnitTopicSelectorProps {
  grade: number;
  courseType: BaseChatDtoCourse;
  selectedTopics: number[];
  setSelectedTopics: (topicIds: number[], topicNames?: string[]) => void;
  expandOnStart?: boolean;
}

export const UnitTopicSelector = ({
  grade,
  courseType,
  selectedTopics,
  setSelectedTopics,
  expandOnStart = true,
}: UnitTopicSelectorProps) => {
  const { data: allTopics, isLoading } = useGetTopics(
    {
      grade,
      courseType,
    },
    {
      query: {
        enabled: !!(grade && courseType),
      },
    },
  );

  const [searchTerm, setSearchTerm] = useState("");
  const { t } = useTranslation();

  const allTopicsAsOption = useMemo(() => {
    const result: {
      label: string;
      options: { id: number; label: string }[];
    }[] = [];

    allTopics?.topics?.forEach((topic) => {
      const search = result.find((a) => a.label === topic.chapterName);
      if (search) {
        search.options.push({
          id: topic.id,
          label: topic.topic,
        });
      } else {
        result.push({
          label: topic.chapterName,
          options: [
            {
              id: topic.id,
              label: topic.topic,
            },
          ],
        });
      }
    });
    return result;
  }, [allTopics]);

  const filteredTopics = useMemo(() => {
    if (!searchTerm) return allTopicsAsOption;

    const lowercaseSearch = searchTerm.toLowerCase();
    return allTopicsAsOption
      .map((unit) => ({
        ...unit,
        options: unit.options.filter((topic) =>
          topic.label.toLowerCase().includes(lowercaseSearch),
        ),
      }))
      .filter(
        (unit) =>
          unit.label.toLowerCase().includes(lowercaseSearch) ||
          unit.options.length > 0,
      );
  }, [allTopicsAsOption, searchTerm]);

  const [expandedUnit, setExpandedUnit] = useState<string | null>(null);

  // Set first unit as expanded initially and after search
  useEffect(() => {
    if (filteredTopics.length > 0 && expandOnStart) {
      setExpandedUnit(filteredTopics[0].label);
    }
  }, [JSON.stringify(filteredTopics)]);

  if (!allTopicsAsOption?.length || isLoading) {
    const shouldAnimate = isLoading && !allTopicsAsOption?.length;
    return (
      <div className="w-full border rounded-lg overflow-hidden dark:border-gray-700">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="border-b last:border-b-0 dark:border-gray-700"
          >
            <div className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 flex items-center justify-between">
              <div
                className={`h-4 w-32 rounded ${shouldAnimate && " bg-gray-200 dark:bg-gray-700 animate-pulse"}`}
              >
                {" "}
              </div>

              <div className="flex items-center space-x-2">
                <div
                  className={`h-3 w-8 rounded ${shouldAnimate && "bg-gray-200 dark:bg-gray-700 animate-pulse"}`}
                ></div>
                <div
                  className={`h-4 w-4 rounded ${shouldAnimate && " bg-gray-200 dark:bg-gray-700 animate-pulse"}`}
                ></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const toggleUnit = (unitLabel: string) => {
    setExpandedUnit((prev) => (prev === unitLabel ? null : unitLabel));
  };

  const isTopicSelected = (topicId: number) => selectedTopics.includes(topicId);

  const getUnitSelectionState = (unit: (typeof filteredTopics)[0]) => {
    const selectedCount = unit.options.filter((topic) =>
      selectedTopics.includes(topic.id),
    ).length;
    if (selectedCount === 0) return "none";
    if (selectedCount === unit.options.length) return "all";
    return "some";
  };

  const getTopicNamesByIds = (topicIds: number[]) => {
    return allTopicsAsOption
      .flatMap((unit) => unit.options)
      .filter((topic) => topicIds.includes(topic.id))
      .map((topic) => topic.label);
  };

  const toggleAllTopicsInUnit = (unit: (typeof filteredTopics)[0]) => {
    const unitState = getUnitSelectionState(unit);
    const topicIds = unit.options.map((topic) => topic.id);

    if (unitState === "all") {
      // Deselect all topics in unit
      const newSelectedTopics = selectedTopics.filter(
        (id) => !topicIds.includes(id),
      );
      setSelectedTopics(
        newSelectedTopics,
        getTopicNamesByIds(newSelectedTopics),
      );
    } else {
      // Select all topics in unit in addition to already selected topics,
      // avoid duplicates
      const newSelectedTopics = [...new Set([...selectedTopics, ...topicIds])];
      setSelectedTopics(
        newSelectedTopics,
        getTopicNamesByIds(newSelectedTopics),
      );
    }
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t("common.search_unit_or_topic")}
          className="w-full px-4 py-2 border rounded-lg dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
      </div>

      <div className="w-full border rounded-lg overflow-hidden dark:border-gray-700">
        {filteredTopics.map((unit, index) => {
          const unitState = getUnitSelectionState(unit);
          return (
            <div
              key={unit.label + index}
              className="border-b last:border-b-0 dark:border-gray-700"
            >
              {/* Unit Header */}
              <div className="flex">
                <Button
                  onClick={() => toggleAllTopicsInUnit(unit)}
                  variant="solid"
                  size="md"
                  bgColor="bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  {unitState === "none" && (
                    <Circle size={16} className="text-gray-400" />
                  )}
                  {unitState === "some" && (
                    <MinusCircle size={16} className="text-orange-500" />
                  )}
                  {unitState === "all" && (
                    <CheckCircle size={16} className="text-orange-500" />
                  )}
                </Button>
                <Button
                  onClick={() => toggleUnit(unit.label)}
                  variant="solid"
                  size="md"
                  bgColor="bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                  fullWidth
                  className="flex justify-between"
                  aria-expanded={expandedUnit === unit.label}
                >
                  <Text variant="p" className="text-left">
                    {unit.label}
                  </Text>
                  <div className="flex items-center space-x-2">
                    <Text
                      variant="p"
                      size="sm"
                      status="secondary"
                      className="tabular-nums"
                    >
                      {
                        unit.options.filter((topic) =>
                          selectedTopics.includes(topic.id),
                        ).length
                      }
                      /{unit.options.length}
                    </Text>
                    <ChevronDown
                      size={16}
                      className={`text-gray-400 dark:text-gray-500 transition-transform ${
                        expandedUnit === unit.label ? "rotate-180" : ""
                      }`}
                    />
                  </div>
                </Button>
              </div>

              {/* Unit Topics */}
              {expandedUnit === unit.label && (
                <div className="relative">
                  <div className="bg-white dark:bg-gray-800 px-4 py-2 max-h-[250px] overflow-y-auto pb-6">
                    {unit.options.map((topic) => (
                      <Button
                        data-testid="create-assignment-from-topic-selector-topic"
                        key={topic.id}
                        onClick={() => {
                          if (selectedTopics.includes(topic.id)) {
                            const newSelectedTopics = selectedTopics.filter(
                              (id) => id !== topic.id,
                            );
                            setSelectedTopics(
                              newSelectedTopics,
                              getTopicNamesByIds(newSelectedTopics),
                            );
                          } else {
                            const newSelectedTopics = [
                              ...new Set([...selectedTopics, topic.id]),
                            ];
                            setSelectedTopics(
                              newSelectedTopics,
                              getTopicNamesByIds(newSelectedTopics),
                            );
                          }
                        }}
                        variant="solid"
                        size="xs"
                        rounded="lg"
                        fullWidth
                        bgColor={
                          isTopicSelected(topic.id)
                            ? "bg-orange-50 dark:bg-orange-900/30"
                            : "hover:bg-gray-100 dark:hover:bg-gray-700"
                        }
                        textColor={
                          isTopicSelected(topic.id)
                            ? "text-orange-600 dark:text-orange-300"
                            : ""
                        }
                        className="my-0.5 text-left focus:outline-none"
                      >
                        <Text variant="p" size="sm">
                          {topic.label}
                        </Text>
                      </Button>
                    ))}
                  </div>
                  {unit.options.length > 5 && (
                    <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white dark:from-gray-800 to-transparent pointer-events-none" />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
